import React from "react";
import { Icon, notify } from "./registry";
/* ============================================================
   FW Admin → Ops & Health → App errors
   The app's own crashes (lib/crash.ts → server/src/clienterrors.js,
   migration #38): one row per fingerprint, the busiest over the last 7 days
   first, with a 14-day sparkline, the release it first showed up on, how
   many accounts it hit, the stack, and Resolve / Ignore. A resolved error
   that comes back on a newer release reopens by itself.
   ============================================================ */
const api = (): any => (window as any).VFP_DB && (window as any).VFP_DB.clientErrors;
const STATUS_LABEL: any = { open: "Open", resolved: "Resolved", ignored: "Ignored" };

function ago(v: any) {
  const t = v ? Date.parse(v) : NaN;
  if (!Number.isFinite(t)) return "—";
  const s = Math.max(0, (Date.now() - t) / 1000);
  return s < 90 ? "just now" : s < 3600 ? Math.round(s / 60) + "m ago" : s < 86400 ? Math.round(s / 3600) + "h ago" : Math.round(s / 86400) + "d ago";
}
/* "20260925T1432-ab12cd34" → "Sep 25 14:32 · ab12cd34" (the build's UTC time + commit) */
function releaseLabel(r: any) {
  const m = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(?:-(\w+))?$/.exec(String(r || ""));
  if (!m) return r || "unknown";
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" }) + " " + m[4] + ":" + m[5] + " UTC" + (m[6] ? " · " + m[6] : "");
}

/* 14 days of counts as thin bars on one baseline; the last 7 (what the list
   sorts by) in teal, the week before recessive. Each bar says its day and
   count on hover. */
function Sparkline({ series }: any) {
  const vals: number[] = (series || []).map((n: any) => Number(n) || 0);
  const max = Math.max(1, ...vals);
  const W = 4, GAP = 2, H = 24;
  const today = Date.now();
  return (
    <svg width={vals.length * (W + GAP) - GAP} height={H} viewBox={`0 0 ${vals.length * (W + GAP) - GAP} ${H}`} role="img"
      aria-label={`Last 14 days: ${vals.join(", ")}`} style={{ display: "block", flexShrink: 0 }}>
      <line x1={0} x2={vals.length * (W + GAP)} y1={H - 0.5} y2={H - 0.5} stroke="var(--line)" strokeWidth={1} />
      {vals.map((v, i) => {
        const h = v ? Math.max(3, Math.round((v / max) * (H - 2))) : 0;
        const day = new Date(today - (vals.length - 1 - i) * 86_400_000).toLocaleDateString("en-US", { month: "short", day: "numeric" });
        return (
          <g key={i}>
            <title>{`${day}: ${v}`}</title>
            <rect x={i * (W + GAP)} y={0} width={W + GAP} height={H} fill="transparent" />
            {h > 0 && <rect x={i * (W + GAP)} y={H - 1 - h} width={W} height={h} rx={1.5} fill={i >= vals.length - 7 ? "var(--teal)" : "var(--fg-faint)"} />}
          </g>
        );
      })}
    </svg>
  );
}

function ErrorRow({ e, busy, onStatus }: any) {
  const [open, setOpen] = React.useState(false);
  const s = e.sample || {};
  const where = [s.route ? "/" + s.route : null, s.browser, s.os, s.device].filter(Boolean).join(" · ");
  return (
    <div style={{ borderTop: "1px solid var(--line)", paddingTop: 10 }}>
      <div className="row gap-10" style={{ alignItems: "flex-start", justifyContent: "space-between" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="mono" style={{ fontSize: 12.5, fontWeight: 700, overflowWrap: "anywhere" }}>{e.message}</div>
          <div className="faint" style={{ fontSize: 11.5, marginTop: 3, lineHeight: 1.5 }}>
            <b className="tnum" style={{ color: "var(--fg-2)" }}>{e.count7d}</b> in 7 days · {e.count} total ·{" "}
            <b className="tnum" style={{ color: "var(--fg-2)" }}>{e.accounts}{e.accountsCapped ? "+" : ""}</b> account{e.accounts === 1 ? "" : "s"}
            {e.signedOut ? ` (+${e.signedOut} signed out)` : ""} · last {ago(e.lastSeen)}
          </div>
          <div className="faint" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
            First seen on {releaseLabel(e.firstRelease)}{e.lastRelease && e.lastRelease !== e.firstRelease ? " · latest " + releaseLabel(e.lastRelease) : ""}
            {e.reopenedAt && e.status === "open" ? <span style={{ color: "var(--amber)" }}> · came back {ago(e.reopenedAt)}</span> : null}
            {e.status !== "open" && e.statusBy ? ` · ${STATUS_LABEL[e.status].toLowerCase()} by ${e.statusBy} ${ago(e.statusAt)}` : ""}
          </div>
          {where && <div className="faint" style={{ fontSize: 11.5 }}>{where}</div>}
        </div>
        <Sparkline series={e.series} />
      </div>
      <div className="row gap-6" style={{ marginTop: 8, flexWrap: "wrap" }}>
        <button className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)} aria-expanded={open}>{open ? "Hide stack" : "Stack"}</button>
        {e.status !== "resolved" && <button className="btn btn-primary btn-sm" disabled={!!busy} onClick={() => onStatus(e, "resolved")}>{busy === e.fingerprint + ":resolved" ? "Resolving…" : "Resolve"}</button>}
        {e.status !== "ignored" && <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => onStatus(e, "ignored")}>Ignore</button>}
        {e.status !== "open" && <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => onStatus(e, "open")}>Reopen</button>}
      </div>
      {open && (
        <pre className="mono" style={{ fontSize: 11, lineHeight: 1.45, margin: "8px 0 0", padding: "8px 10px", background: "var(--surface-2)", border: "1px solid var(--line)", borderRadius: 8, overflow: "auto", maxHeight: 260, whiteSpace: "pre" }}>
          {(s.stack || "No stack was sent.") + (s.componentStack ? "\n\nComponents:" + s.componentStack : "")}
          {s.mapped === false && s.stack ? "\n\n(minified — this build's source maps weren't on the server)" : ""}
        </pre>
      )}
    </div>
  );
}

function AppErrors() {
  const a = api();
  const [status, setStatus] = React.useState<any>("open");
  const [data, setData] = React.useState<any>(null);
  const [err, setErr] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(null);
  const load = React.useCallback(() => {
    if (!a) return;
    setErr("");
    a.list(status).then(setData).catch((e: any) => setErr(String(e.message || e)));
  }, [status]);
  React.useEffect(() => { load(); }, [load]);
  if (!a) return null;

  async function onStatus(e: any, next: string) {
    setBusy(e.fingerprint + ":" + next);
    try {
      await a.setStatus(e.fingerprint, next);
      notify(next === "resolved" ? "Resolved. It reopens by itself if it comes back on a newer release." : next === "ignored" ? "Ignored. It stays out of this list and won't email you." : "Reopened.");
      load();
    } catch (x: any) { setErr(String(x.message || x)); }
    finally { setBusy(null); }
  }

  const errors: any[] = (data && data.errors) || [];
  const counts = (data && data.counts) || {};
  return (
    <div>
      <div className="row between" style={{ marginBottom: 6, gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <div className="kicker">App errors</div>
        <div className="row gap-4" style={{ flexWrap: "wrap" }}>
          {["open", "resolved", "ignored"].map((s) => (
            <button key={s} className={"chip" + (status === s ? " teal" : "")} style={{ cursor: "pointer", fontSize: 11 }} onClick={() => setStatus(s)}>
              {STATUS_LABEL[s]}{counts[s] != null ? " · " + counts[s] : ""}
            </button>
          ))}
          <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px" }} onClick={load} aria-label="Refresh app errors"><Icon name="undo" size={12} /></button>
        </div>
      </div>
      {err && <p className="muted" style={{ fontSize: 12, margin: "0 0 6px", color: "var(--amber)" }}>{err}</p>}
      {!data && !err ? <p className="muted" style={{ fontSize: 12, margin: 0 }}>Loading…</p>
        : data && data.setup === false
          ? <p className="muted" style={{ fontSize: 12, margin: 0, color: "var(--amber)" }}>Run migration #{data.migrationN || 38} (<code>{data.migration}</code>) to keep app errors. Until then the app's crash reports are accepted and dropped.</p>
          : errors.length === 0
            ? <p className="muted" style={{ fontSize: 12, margin: 0 }}>{status === "open" ? "No open app errors. When the app crashes for anyone, it shows up here within seconds." : status === "resolved" ? "Nothing resolved yet." : "Nothing ignored."}</p>
            : <div style={{ display: "grid", gap: 10 }}>
                {errors.map((e: any) => <ErrorRow key={e.fingerprint} e={e} busy={busy} onStatus={onStatus} />)}
              </div>}
      {data && data.setup !== false && data.sourceMaps === false && errors.length > 0 && (
        <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>Stacks are minified: the app build's source maps aren't on this server (CLIENT_SOURCEMAPS_DIR).</p>
      )}
    </div>
  );
}

export { AppErrors, Sparkline, releaseLabel };
